import $ from 'jquery';
import {last} from 'lodash';
import {emptyObject} from '@packages/utilities/fp/utils';
import {
  WF_DATA_ID_ATTR,
  WF_INSTANCEID_ATTR,
  WFID_ATTR,
} from '@packages/utilities/html';
import {VOID_HTML_TAGS} from '@packages/systems/core/constants/SharedConfig';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';

export const TEXT_NODE = 3;
export const NORMAL_NODE = 1;
export const DOCUMENT_NODE = 9;

export type BoundingClientRect = {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
};

/**
 * Validates whether a numeric value corresponds to a valid DOM Node type constant.
 * @param {number} value - The numeric value to validate
 * @returns {boolean} True if the value matches a valid Node type constant
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
 */
function isValidNodeTypeValue(value: number): boolean {
  return (
    value === Node.ELEMENT_NODE ||
    value === Node.ATTRIBUTE_NODE ||
    value === Node.TEXT_NODE ||
    value === Node.CDATA_SECTION_NODE ||
    value === Node.ENTITY_REFERENCE_NODE ||
    value === Node.ENTITY_NODE ||
    value === Node.PROCESSING_INSTRUCTION_NODE ||
    value === Node.COMMENT_NODE ||
    value === Node.DOCUMENT_NODE ||
    value === Node.DOCUMENT_TYPE_NODE ||
    value === Node.DOCUMENT_FRAGMENT_NODE ||
    value === Node.NOTATION_NODE
  );
}

function isNode(value: unknown): value is Node {
  if (!value) return false;

  return (
    typeof value === 'object' &&
    'nodeType' in value &&
    typeof value.nodeType === 'number' &&
    isValidNodeTypeValue(value.nodeType)
  );
}

function isElement(value: unknown): value is Element {
  return isNode(value) && value.nodeType === Node.ELEMENT_NODE;
}

function isTextNode(value: unknown): value is Text {
  return isNode(value) && value.nodeType === Node.TEXT_NODE;
}

/**
 * Determines whether an object is a jQuery object by checking for the presence of the 'jquery' property.
 * Source: https://api.jquery.com/jquery-2/
 *
 * @param {unknown} obj - The object to test for jQuery compatibility
 * @returns {obj is JQuery} True if the object is a jQuery object, false otherwise
 */
function isJQueryObject(obj: unknown): obj is JQuery {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'jquery' in obj &&
    typeof obj.jquery === 'string'
  );
}

/**
 * Gets a reference to an element's parent document.
 *
 * @param  {unknown} node The node to find the document for (can be a native DOM node or jQuery object)
 * @return {Document | null} The document this element is inside or null if the node is not attached to the document
 */
export function getDocument(node?: unknown): Document | null {
  const unwrappedNode = _unwrap(node);

  if (!unwrappedNode) {
    return null;
  }

  return unwrappedNode.ownerDocument;
}

/**
 * Gets a reference to an element's parent window.
 *
 * @param  {unknown} node The node to find the window for (can be a native DOM node or jQuery object)
 * @return {(WindowProxy & typeof globalThis) | null} The window this element is part of, or null if not found
 */
export function getWindow(
  node?: unknown
): (WindowProxy & typeof globalThis) | null {
  const unwrappedNode = _unwrap(node);

  if (!unwrappedNode) {
    return null;
  }

  const doc = unwrappedNode.ownerDocument;

  if (!doc) {
    return null;
  }

  return doc.defaultView;
}

/**
 * Gets a reference to an element's parent iframe.
 *
 * @param  {unknown} node The node to find the iframe for (can be a native DOM node or jQuery object)
 * @return {Element | null} The iframe element the node is inside, or null if it's in the top-level window
 */
export function getIframe(node?: unknown): Element | null {
  const win = getWindow(_unwrap(node));

  if (!win) {
    return null;
  }

  return win.frameElement || null;
}

/**
 * Gets an element's bounds as a plain object.
 *
 * @param  {HTMLElement} element The element to get bounds for
 * @return {BoundingClientRect}  Element bounds
 */
export function getBounds(element: HTMLElement): BoundingClientRect {
  const bounds = element.getBoundingClientRect();
  return {
    top: bounds.top,
    left: bounds.left,
    bottom: bounds.bottom,
    right: bounds.right,
    width: bounds.width,
    height: bounds.height,
  };
}

function hasPositionMode(
  element: HTMLElement,
  positionMode: 'fixed' | 'sticky'
): boolean {
  let parent = element;
  do {
    const style = window.getComputedStyle(parent);
    if (style.position === positionMode) {
      return true;
    }
    // @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'HTMLElement'.
  } while ((parent = parent.offsetParent));

  return false;
}

/**
 * Walks up the parent chain and determines if this element is inside a fixed element.
 * @param  {Element}  element A native DOM element.
 * @return {boolean}          True if the element (or any of its parents) are position: fixed.
 */
export function isPositionFixed(element: HTMLElement): boolean {
  return hasPositionMode(element, 'fixed');
}

/**
 * Walks up the parent chain and determines if this element is inside a sticky element.
 * @param  {Element}  element A native DOM element.
 * @return {boolean}  True if the element (or any of its parents) are position: sticky.
 */
export function isPositionSticky(element: HTMLElement): boolean {
  return hasPositionMode(element, 'sticky');
}

/**
 * Gets the data-w-id attribute value from the node.
 *
 * @param  {unknown} node The node to get the Webflow ID from (can be a native DOM node or jQuery object)
 * @return {string | null} The Webflow ID attribute value, or null if not found
 */
export function getNativeId(node?: unknown): string | null {
  if (!node) {
    return null;
  }
  const unwrappedNativeNode = _unwrap(node);

  if (!unwrappedNativeNode) return null;

  if (isTextNode(unwrappedNativeNode)) {
    return null;
  }

  if (!isElement(unwrappedNativeNode)) {
    return null;
  }

  const element = unwrappedNativeNode;

  const webflowId =
    element.getAttribute(WFID_ATTR) ||
    element.getAttribute(WF_INSTANCEID_ATTR) ||
    '';

  return webflowId || null;
}

type RenameAttributeKeyOptions = {
  preserveOrder?: boolean;
};

/**
 * Rename an attribute key from a given element.
 *
 * @param {unknown} node The node to rename one of its attributes (can be a native DOM node or jQuery object)
 * @param {string} sourceKey The original attribute key to rename from
 * @param {string} targetKey The new attribute key to rename to
 * @param {RenameAttributeKeyOptions} options Configuration options including preserveOrder
 */
export function renameAttributeKey(
  node: unknown,
  sourceKey: string,
  targetKey: string,
  {preserveOrder = false}: RenameAttributeKeyOptions = emptyObject
): void {
  const unwrappedNativeNode = _unwrap(node);

  if (!unwrappedNativeNode) {
    return;
  }

  if (!isElement(unwrappedNativeNode)) {
    return;
  }

  const element = unwrappedNativeNode;

  const value = element ? element.getAttribute(sourceKey) : null;

  if (value !== null && sourceKey !== targetKey) {
    // If attribute order needs to be preserved, we need to remove / rename / re-add attributes since
    // the DOM API doesn't give us an API to rename an attribute in place or insert one at an index
    if (preserveOrder) {
      const attributes: Array<{
        key: string;
        value: string;
      }> = [];

      // Collect current attributes in their native DOM order
      for (const attr of element.attributes) {
        attributes.push({
          key: attr.nodeName,
          value: attr.nodeValue ?? '',
        });
      }

      attributes.forEach((attr) => {
        // Remove existing attribute based on the existing key
        element.removeAttribute(attr.key);

        // Rename the attribute if it matches the sourceKey
        const key = attr.key === sourceKey ? targetKey : attr.key;

        // Re-add the attributes in the original order, but this time with the new targetKey for the renamed attr
        element.setAttribute(key, attr.value);
      });
    } else {
      element.setAttribute(targetKey, value);
      element.removeAttribute(sourceKey);
    }
  }
}

/**
  This util is used to replace the data-w-id attribute for the data-w-instance-of attribute.

  Its usage is for root component elements to mimic the behavior of nodes inside instances.
  For example, headings, links, images, etc. that are converted into components should behave like they are inside a component wrapper (div, section, etc.).
  It's main purpose is to have these nodes working with the Content Editor code that becomes active with the on-canvas editing double-click feature.
  
  @param {HTMLElement} nativeNode The HTML element to modify
  @param {string} instanceOfId The instance ID to set as the new attribute value
  @return {NamedNodeMap | null} The element's attribute map after modification, or null if no document found
 */
export const removeWfIdInFavorOfInstanceAttr = (
  nativeNode: HTMLElement,
  instanceOfId: string
) => {
  const doc = getDocument(_unwrap(nativeNode));
  if (!doc) {
    return null;
  }
  const attrMap = nativeNode.attributes;
  if (nativeNode.hasAttribute(WFID_ATTR)) {
    nativeNode.removeAttribute(WFID_ATTR);
  }
  const instanceAttr = doc.createAttribute(WF_INSTANCEID_ATTR);
  instanceAttr.value = instanceOfId;
  attrMap.setNamedItem(instanceAttr);
  return attrMap;
};

/**
  This util is used to replace the data-w-instance-of attribute for the data-w-id attribute.

  Its usage is for root component elements to mimic the behavior of nodes inside instances.
  For example, headings, links, images, etc. that are converted into components should behave like they are inside a component wrapper (div, section, etc.).
  It's main purpose is to have these nodes working with the Content Editor code that becomes active with the on-canvas editing double-click feature.
  
  @param {HTMLElement} nativeNode The HTML element to modify
  @param {string} wfId The Webflow ID to set as the new attribute value
  @return {NamedNodeMap | null} The element's attribute map after modification, or null if no document found
 */
export const removeInstanceInFavorOfWIdAttr = (
  nativeNode: HTMLElement,
  wfId: string
) => {
  const doc = getDocument(_unwrap(nativeNode));
  if (!doc) {
    return null;
  }
  const attrMap = nativeNode.attributes;
  if (nativeNode.hasAttribute(WF_INSTANCEID_ATTR)) {
    nativeNode.removeAttribute(WF_INSTANCEID_ATTR);
  }
  const wfIdAttr = doc.createAttribute(WFID_ATTR);
  wfIdAttr.value = wfId;
  attrMap.setNamedItem(wfIdAttr);
  return attrMap;
};

/**
 * Reads the "class" attribute from a native node.
 * Need to do this because HTML forms auto-assign [form-el].[input-id] as an expando property on the <form>,
 * so if there's an input with id="className" inside a form, referencing <form>.className actually returns the input instead of the form's classes!
 *
 * @param {Element | null} nativeNode The element to read classes from
 * @return {string[]} Array of class names split from the class attribute
 */
export function getNativeClasses(nativeNode?: null | Element): Array<string> {
  if (!nativeNode || isTextNode(nativeNode)) {
    return [];
  }

  const rawClassAttr = nativeNode.getAttribute('class') || '';
  return rawClassAttr
    .trim()
    .split(/\s+/g)
    .filter((s) => s.length);
}

/**
 * Walks up the DOM tree until a node that matches the filter is found.
 *
 * @param  {unknown} startNode The node to start with (can be a native DOM node or jQuery object)
 * @param  {Function} filter The filter function to check whether a node matches search criteria
 * @return {Node | null} The closest parent node that matches the filter, or null if not found
 */
export function closest(startNode: unknown, filter: (arg1: Node) => boolean) {
  const unwrappedStartNode = _unwrap(startNode);

  if (!unwrappedStartNode) {
    return null;
  }

  let parentNode: Node | null = unwrappedStartNode;

  while (parentNode) {
    if (filter(parentNode)) {
      return parentNode;
    }

    parentNode = parentNode.parentNode;
  }

  return null;
}

/**
 * Determines whether a tag can not take any children elements (e.g. images, inputs, hr, etc.).
 *
 * @param  {string} tagName The name of the tag, in lowercase
 * @return {boolean} True if this is a tag that can't contain any children
 */
export function isClosed(tagName: string) {
  return VOID_HTML_TAGS.has((tagName || '').toLowerCase());
}

/**
 * Gets the offset of an element prior to CSS transforms.
 * @param  {HTMLElement} _$el A jQuery-wrapped HTML element.
 * @return {JQuery.Coordinates | null} The element's offset without transforms.
 */
export function getRealOffset(_$el?: HTMLElement): JQuery.Coordinates | null {
  if (!_$el) {
    return null;
  }

  const $el = $(_$el);

  const transformMatrix = $el.css('transform');
  const hasTransform = transformMatrix && transformMatrix !== 'none';

  const htmlElement = $el[0];

  if (!htmlElement) {
    return null;
  }

  const nativeStyle = htmlElement.style;
  const inlineTrans = nativeStyle.transform || nativeStyle.webkitTransform;

  // Temporarily remove the transformation
  if (hasTransform) {
    $el.css('transform', 'none');
  }

  // Read the element's offset
  const offset = $el.offset() || null;

  // Remove inline override - should revert back to CSS definition
  if (hasTransform) {
    $el.css('transform', inlineTrans || '');
  }

  return offset;
}

/**
 * Determines if a keyboard event occurs inside a form element.
 *
 * @param  {Event} e The keyboard event
 * @return {boolean} True if the key is pressed inside a form element
 */
export function isFormEvent(e: Event) {
  const isFormElement =
    e.target instanceof HTMLFormElement ||
    e.target instanceof HTMLSelectElement ||
    e.target instanceof HTMLButtonElement;

  const isInputOrTextArea =
    e.target instanceof HTMLInputElement ||
    e.target instanceof HTMLTextAreaElement;

  const hasForm = (isFormElement || isInputOrTextArea) && e.target.form;

  const isContentEditable =
    e.target instanceof HTMLElement && e.target.isContentEditable;

  const isInsideEmojiPicker = e
    .composedPath()
    //emoji-mart provides a custom html element <em-emoji-picker/> which is what we're targeting here
    .some(
      (node) =>
        node instanceof HTMLElement && node.tagName === 'EM-EMOJI-PICKER'
    );

  return (
    hasForm || isInputOrTextArea || isContentEditable || isInsideEmojiPicker
  );
}

/**
 * Makes sure a jQuery-wrapped node is unwrapped.
 *
 * @param {unknown} value The value to unwrap
 * @return {Node | null} A native DOM node, or null if the value is not a valid Node or jQuery object
 */
function _unwrap(value: unknown): Node | null {
  if (!value) {
    return null;
  }

  if (isJQueryObject(value)) {
    return value.get(0) || null;
  }

  if (isNode(value)) {
    return value;
  }

  return null;
}

/**
 * Walks up the DOM tree from a given event target to find the closest ancestor that is a link.
 *
 * @param {EventTarget} eventTarget The DOM event target to start the search from
 * @returns {Node | null} The closest ancestor node with an 'href' attribute, or null if not found
 */
export const getNativeLinkNodeFromEventTarget = (
  eventTarget: EventTarget
): Node | null => {
  return closest(eventTarget, function (nativeNodeParent) {
    return isElement(nativeNodeParent)
      ? Boolean(nativeNodeParent.getAttribute('href'))
      : false;
  });
};

/**
 * Given a DOM element from the Canvas, get its NativeId.
 *
 * @param {Element} domElement The DOM element to extract the native ID from
 * @param {NativeId[]} focusedSymbolNativeIdPath The path of focused symbol native IDs
 * @return {NativeId | null | undefined} The native ID for the element, or null/undefined if not found
 */
export const getNativeIdFromDomElement = (
  domElement: Element,
  focusedSymbolNativeIdPath: NativeId[]
): NativeId | null | undefined => {
  const idPathString = domElement.getAttribute(WF_DATA_ID_ATTR);

  if (!idPathString) {
    return null;
  }

  const idPath: Array<NativeId> = JSON.parse(idPathString);
  const focusedElementId = last(focusedSymbolNativeIdPath);

  // If `focusedElementId` is specified, we only want to return the ID of the
  // node that is a _direct child_ of the focused symbol.
  //
  // For example, if our tree has a hierarchy like so:
  //
  // ```
  // Page =
  //   <Body id="Body-0">
  //     <SymbolC id="SymbolC-0" />
  //   </Body>
  //
  // SymbolA =
  //   <Heading id="Heading-0">Hay 🐴</Heading>
  //
  // SymbolB =
  //   <SymbolA id="SymbolA-0" />
  //
  // SymbolC =
  //   <div id="div-1234-abcd">
  //     <SymbolB id="SymbolB-0" />
  //     <SymbolB id="SymbolB-1" />
  //     <SymbolB id="SymbolB-2" />
  //   </div>
  // ```
  //
  // Then our _rendered_ DOM hierarchy would look like this:
  //
  // ```
  // <body data-wf-id=["Body-0"]>
  //   <div data-wf-id=[
  //     "SymbolC-0",
  //     "div-1234-abcd",
  //   ]>
  //     <h1 data-wf-id=[
  //       "SymbolC-0",
  //       "SymbolB-0",
  //       "SymbolA-0",
  //       "Heading-0",
  //     ]>
  //       Hay 🐴
  //     </h1>
  //
  //     <h1 data-wf-id=[
  //       "SymbolC-0",
  //       "SymbolB-1",
  //       "SymbolA-0",
  //       "Heading-0",
  //     ]>
  //       Hay 🐴
  //     </h1>
  //
  //     <h1 data-wf-id=[
  //       "SymbolC-0",
  //       "SymbolB-2",
  //       "SymbolA-0",
  //       "Heading-0",
  //     ]>
  //       Hay 🐴
  //     </h1>
  //   </div>
  // </body>
  // ```
  //
  // Now, suppose we hover our mouse over the second `<h1>` on the page.
  //
  // - If no `focusedElementId` is specified, we want to return the outermost
  //   symbol, or `SymbolC-0`, the zeroth ID in our `idPath`.
  //
  // - If `focusedElementId` is `SymbolC-0`, we want to return the _next_
  //   deepest symbol wrapper, concretely `SymbolB-1` which corresponds to the
  //   second instance of `SymbolB` inside `SymbolC`'s implementation.
  //
  // - If `focusedElementId` is `SymbolB-1` (i.e. we've focused into `SymbolC`
  //   then the second `SymbolB` instance), then we want to return the inner
  //   `SymbolC` instance ID, or `SymbolC-0`.
  //
  // - If `focusedElementId` is `SymbolB-0`, then we want to return `Heading-0`.
  //
  // - Finally, if `focusedElementId` is set but does not correspond to anything
  //   inside our `idPath`, it means that we're hovering over an element
  //   _outside_ of the focused symbol. In this case, we want to return the
  //   outermost ID in our path, or `idPath[0]`.
  //
  // The good news: It's pretty simple to implement this by always returning the
  // ID that immediately follows `focusedElementId` within `idPath`.
  const focusedIdIndex = focusedElementId
    ? idPath.indexOf(focusedElementId)
    : -1;

  // If `focusedIdIndex` is -1, we return the outermost wrapper ID (at index 0):
  return idPath[focusedIdIndex + 1];

  // FIXME: This logic is disabled because it needlessly adds instance suffixes
  // to IDs when we have multiple instances of the same symbol on the page.
  //
  // Re-enable this logic to fix hoverability/selectability of child elements
  // nested under KitchenSink, which do not automatically have `instance`
  // suffixes:
  //
  // ```js
  // if (!id || !body) return null;
  //
  // const body = domElement.ownerDocument.body;
  //
  // const elementsWithSameId = body.querySelectorAll(`[${WFID_ATTR}="${id}"]`);
  //
  // if (elementsWithSameId.length < 2) {
  //   return id;
  // }
  //
  // const index = Array.prototype.indexOf.call(elementsWithSameId, domElement);
  // return generateInstanceNativeId(id, index);
  // ```js
};
